/**
 * Functions related to creating the "DropCheck" component, as well as fixing the a11y issues. The DropCheck component is a
 * conglomerate of a multi-select dropdown with autocomplete functionality, containing a collection of checkboxes and/or
 * radio buttons. Multiselects are difficult to make a11y-compliant... in this case the autocomplete functionality and the
 * checkbox/radio buttons make it even more difficult.
 *
 * NOTE 1:
 * I used the following A360 article in remediating these dropchecks: https://hub.accessible360.com/kb/articles/438. I think
 * I was able to address most of the issues; however, as of yet we have not addressed the "focus shall not leave trigger bar"
 * requirement. Also, the fact that we have a text field (for autocomplete) AND a separate button (chevron) acting as a trigger
 * to display the dropdown may end up being an issue.
 *
 * NOTE 2:
 * Most of the functions related to Dropchecks were found in GCHomeCommon.js. Since that file is huge already, and for convenience
 * of development, I have moved a lot of those functions to this file (outside of the A11yDropcheck const declaration). Some of
 * those legacy functions simply make subsequent calls to newly refactored A11yDropcheck versions.
 *
 * NOTE 3:
 * For the first iteration of this, I concentrated on those dropcheck components created by calling GCHomeCommon.CreateDropCheck().
 * I noticed there are other slightly different versions of that function (e.g. CreateDropCheckAll()) which have yet to be addressed.
 * Those similar functions should be moved here as well when we get to them. Incidentally, I also noticed that there are quite a
 * few one-off copy/paste implementations of the CreateDropCheck functions sprinkled throughout the code. These have yet to be
 * addressed, too.
 *
 * NOTE 4:
 * I am aware of the significant number of Sonar flags throughout this file. These have come from the migration of the functions
 * from GCHomeCommon.js to here, and while I fixed a bunch of them, there are still quite a few that are too risky to attempt to
 * fix at the moment.
 */
const A11yDropcheck = (function () {

    let _LOG_COLOR = 'blueviolet';

    return {
        LOG_COLOR: _LOG_COLOR,

        checkAll: _checkAll,
        createDropcheck: _createDropcheck,
        createDropcheckAll: _createDropcheckAll,
        deselectAll: _deselectAll,
        dropcheckClicked: _dropcheckClicked,
        dropcheckClickedByProgram: _dropcheckClickedByProgram,
        dropcheckColumnSelectedShow: _dropcheckColumnSelectedShow,
        dropcheckItemClicked: _dropcheckItemClicked,
        dropcheckSetAriaActiveDescendant: _dropcheckSetAriaActiveDescendant,
        dropcheckTriggerBtnOnClick: _dropcheckTriggerBtnOnClick,
        navigateToCheckbox: _navigateToCheckbox,
        navigateListboxWithArrowKeys: _navigateListboxWithArrowKeys,
        selectAllItems: _selectAllItems,
        selectItemByIndex: _selectItemByIndex,
        setDisabled: _setDisabled,
        showDrop: _showDrop,
        textChanged: _textChanged,
        destroyKendoDropdown: _destroyKendoDropdown
    };

    /**
     * This function creates a "dropcheck" dropdown, which is a combination autocomplete/multiselect. The original version violated a lot of
     * a11y guidelines, so this is a refactored version to fix those. The guts of the original function in GCHomeCommon.CreateDropCheck() have
     * been removed from that original function, and a call is made to this new version of the function instead.
     * @param {any} selectId
     * @param {any} configObj
     *                  url - the url to call to fetch the DDL data
     *                  ddlId - id of the DDL element
     *                  tbId - tabindex of the ddl
     *                  textField - field representing the text to display
     *                  textValue - field representing the ID
     *                  cValues - pre-selected values to be checked
     *                  dropdownType - checkboxes (M) or radios (S) (default: M/checkboxes)
     *                  isAsync - boolean flag async or sync
     *                  ddlCacheCtrlID - id of cached data
     *                  durationInMins - duration of data being held in cache
     *                  describedById - element id of aria-describedby attribute
     *                  isCheckAll - flag indicating you want all items in dropdown selected by default (default: false)
     *                  callbackFunction - function to call after DDL is completely loaded,
     *                  maxSelectionsToDisplay - max num of selected items to display before it changes to 'X items checked' (default: 3)
     *                  hiddenTextDelimiter - optional string to use for delimiter for hidden fields which track which dropcheck options have been selected (default: comma ",")
     */
    function _createDropcheck(selectId, configObj)
    {
        // unpack the configObj
        let url = configObj.url;
        let ddlId = configObj.ddlId;
        let tbId = configObj.tbId;
        let cValues = configObj.cValues;
        let dropdownType = configObj.dropdownType;
        let textField = configObj.textField;
        let textValue = configObj.textValue;
        let isAsync = configObj.isAsync;
        let ddlCacheCtrlID = configObj.ddlCacheCtrlID;
        let durationInMins = configObj.durationInMins;
        let describedById = configObj.describedById;
        let callbackFunction = configObj.callbackFunction;
        let isCheckAll = configObj.isCheckAll;
        let maxSelectionsToDisplay = configObj.maxSelectionsToDisplay;
        let hiddenTextDelimiter = configObj.hiddenTextDelimiter || ',';

        let describedByAttr = describedById != null && describedById.length > 0 ? " aria-describedby='" + describedById + "'" : '';
        let inputId = ddlId + 'val';
        let triggerBtnId = ddlId + 'Btn';
        let listboxId = ddlId + 'chk';
        let controlType = dropdownType === 'S' ? 'radio' : 'checkbox';
        let spFlagData = configObj.spFlag;
        
        _logDebug('createDropcheck(): instantiating checkbox dropdown for ID [' + selectId + ']', { configObj: configObj, inputId: inputId, triggerBtnId: triggerBtnId, listboxId: listboxId, controlType: controlType } );

        _setupAjax(!isAsync, false);

        let widthList = $('#' + selectId).attr('data-CtrlListWidth');
        let width = widthList !== undefined ? widthList : $('#' + selectId).width();

        $('#' + selectId).addClass('ah-dropcheck').attr({ 'data-ddlId': ddlId, 'data-CtrlType': controlType });

        if (textField === undefined || textField === null) {
            textField = 'Text';
            textValue = 'Value';
        }
        
        // create a text field, a trigger button, some hidden input fields, and an unordered list wrapper to hold our dropdown items
        $('#' + selectId).addClass('ah-dropcheck').attr('role','application');
        let htm = "<input id='" + inputId + "' type='text' class='ah-dropcheck-text TabHtmlMultiDropDown droptexthide' data-id='" + ddlId + "' autocomplete='off' onclick='A11yDropcheck.showDrop(this)' style='color:darkgrey;' placeHolder='Select' title='Select' onkeyup='A11yDropcheck.textChanged(this, event)'" + describedByAttr + " aria-autocomplete='list' aria-controls='" + listboxId + "' aria-haspopup='listbox' aria-expanded='false' />";
        htm = htm + "<button id='" + triggerBtnId + "' aria-describedby='" + inputId + "' class='ah-dropcheck-btn' role='combobox' aria-expanded='false' aria-haspopup='listbox' onclick='A11yDropcheck.dropcheckTriggerBtnOnClick(event, this, \"" + inputId + "\", \"" + listboxId + "\");'><i class='fas fa-chevron-down' aria-hidden='true' style='font-size:12px;'/></button>";
        htm = htm + "<input type='hidden' class='ah-dropcheck-input-hdn' id='" + ddlId + "hdn' value='' data-delimiter='" + hiddenTextDelimiter + "' />";
        htm = htm + "<input type='hidden' class='customFieldCls' id='" + ddlId + "hdntxt' value='' data-delimiter='" + hiddenTextDelimiter + "' />";
        htm = htm + "<span class='sr-only' id='" + ddlId + "Span'></span>";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdnurl' value='" + url + "'/>";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdnlist' value=''/>";
        htm = htm + "<ul id='" + listboxId + "' role='listbox' aria-multiselectable='true' class='dropcheck' style='display:none;width:" + width + "px !important' tabindex='" + tbId + "'>";
        htm = htm + "<div id='" + ddlId + "chkchk'  class='dropcheck1' style='display:none;' tabindex='" + tbId + "'></div>";

        cValues = cValues != null && cValues !== '' ? cValues.toString().replace(', ', ',').split(',') : '';

        let ddlConfigObj = {
            ddlId: ddlId,
            cValues: cValues,
            textField: textField,
            textValue: textValue,
            controlType: controlType,
            htm: htm,
            isCheckAll: isCheckAll,
            maxSelectionsToDisplay: maxSelectionsToDisplay
        };

        if (spFlagData) {
            _constructDropcheckList(selectId, spFlagData, ddlConfigObj);
            setCheckBoxPermssion();
            A11yForms.fixRequiredFields('#' + inputId + ':not([required])');
            if (callbackFunction) {
                callbackFunction();
            }
        } else {
            // get the dropdown items and then call function to finish constructing the dropdown
            Promise.resolve(_getDdlData(url, ddlCacheCtrlID, durationInMins, true))
                .then(function (data) {
                    _constructDropcheckList(selectId, data, ddlConfigObj);
                    setCheckBoxPermssion();
                    A11yForms.fixRequiredFields('#' + inputId + ':not([required])');
                    if (callbackFunction) {
                        callbackFunction();
                    }
                });
        }

        _setupAjax(!isAsync, true);
        
        // hide the dropdown on mouseleave
        $(document).off('mouseleave', '#' + selectId).on('mouseleave', '#' + selectId, function () {
            if (typeof (isProfileChecked) != 'undefined' && isProfileChecked && selectId === "ddlProfile") {
                loadGridDataBasedOnProfiles();
                isProfileChecked = false;   // NOSONAR (this variable is declared in a different file)
            }

            if ($(this).attr('type') !== 'checkbox') {
                $('.divTabHtmlMultiDropDown').removeClass('color-MultiSelectDdl');
                let ctrlId = $(this).attr('data-ddlId');
                let ctrlType = $(this).attr('data-CtrlType');

                _dropcheckColumnSelectedShow(ctrlId, 0, ctrlType, maxSelectionsToDisplay);

                $(".dropcheck").hide();

                _dropCheckTriggerBtnToggleExpanded($('#' + triggerBtnId), listboxId, inputId);
            }
        });
    }

    function setCheckBoxPermssion() {
        if (checkissensitive === 'True') {
            $('#includeMedSensDignosis').prop('disabled', true);
        }
        else {
            $('#includeMedSensDignosis').prop('disabled', false);
        }
    }
    /**
     * This function creates a "dropcheck" dropdown with an optional "Select All" option The original version violated a lot of
     * a11y guidelines, so this is a refactored version to fix those. The guts of the original function in
     * GCHomeCommonRef.CreateDropCheckAll() have been removed from that original function, and a call is made to this new version
     * of the function instead.
     * @param {any} selectId
     * @param {any} configObj
     */
    function _createDropcheckAll(selectId, configObj) {
        // unpack the configObj
        let url = configObj.url;
        let ddlId = configObj.ddlId;
        let tbId = configObj.tbId;
        let cValues = configObj.cValues;
        let dropdownType = configObj.dropdownType;
        let textField = configObj.textField;
        let textValue = configObj.textValue;
        let isAsync = configObj.isAsync || false;
        let isCheckAll = configObj.isCheckAll;
        let isSelectRequired = configObj.isSelectRequired;
        let isSetCache = configObj.isSetCache;
        let durationInMins = configObj.durationInMins;
        let callbackFunction = configObj.callbackFunction;
        let maxSelectionsToDisplay = configObj.maxSelectionsToDisplay;
        let hiddenTextDelimiter = configObj.hiddenTextDelimiter || ',';

        let ddlCacheCtrlID = url.replace(baseURL + "/", "");
        let inputId = ddlId + 'val';
        let triggerBtnId = ddlId + 'Btn';
        let listboxId = ddlId + 'chk';
        let controlType = dropdownType === 'S' ? 'radio' : 'checkbox';
        let dropcheckContainer = $("#" + selectId);

        _logDebug('_createDropcheckAll(): instantiating checkbox dropdown for ID [' + selectId + ']', { configObj: configObj, inputId: inputId, triggerBtnId: triggerBtnId, listboxId: listboxId, controlType: controlType });

        let checkedState = isCheckAll ? "checked =\'checked'" : '';

        isSelectRequired = typeof (isSelectRequired) == "undefined" ? true : isSelectRequired;

        _setupAjax(!isAsync, false);

        let widthList = $('#' + selectId).attr('data-CtrlListWidth');
        let width = widthList !== undefined ? widthList : $('#' + selectId).width();

        $('#' + selectId).addClass('ah-dropcheck').attr({ 'data-ddlId': ddlId, 'data-CtrlType': controlType, 'role': 'application' });

        if (textField === undefined || textField === null) {
            textField = 'Text';
            textValue = 'Value';
        }

        // create a text field, a trigger button, some hidden input fields, and an unordered list wrapper to hold our dropdown items
        $('#' + selectId).addClass('ah-dropcheck');
        let htm = "<input id='" + inputId + "' type='text' class='ah-dropcheck-text TabHtmlMultiDropDown droptexthide' data-id='" + ddlId + "' autocomplete='off' onclick='A11yDropcheck.showDrop(this)' style='color:darkgrey;' placeHolder='Select' title='Select' onkeyup='A11yDropcheck.textChanged(this, event)' aria-autocomplete='list' aria-controls='" + listboxId + "' aria-haspopup='listbox' aria-expanded='false' />";
        htm = htm + "<button id='" + triggerBtnId + "' aria-describedby='" + inputId + "' class='ah-dropcheck-btn' role='combobox' aria-expanded='false' aria-haspopup='listbox' onclick='A11yDropcheck.dropcheckTriggerBtnOnClick(event, this, \"" + inputId + "\", \"" + listboxId + "\");'><i class='fas fa-chevron-down' aria-hidden='true' style='font-size:12px;'/></button>";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdn' value='' value='' data-delimiter='" + hiddenTextDelimiter + "' />";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdntxt' value='' value='' data-delimiter='" + hiddenTextDelimiter + "' />";
        htm = htm + "<span class='sr-only' id='" + ddlId + "Span'></span>";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdnurl' value='" + url + "'/>";
        htm = htm + "<input type='hidden' id='" + ddlId + "hdnlist' value=''/>";
        htm = htm + "<ul id='" + listboxId + "' role='listbox' aria-multiselectable='true' class='dropcheck' style='display:none;width:" + width + "px !important' tabindex='" + tbId + "'>";
        htm = htm + "<div id='" + ddlId + "chkchk'  class='dropcheck1' style='display:none;' tabindex='" + tbId + "'></div>";

        let cVal = cValues != null && cValues !== '' ? cValues.toString().replace(', ', ',').split(',') : '';
        let k = 0;

        let cValInt = [];
        $.each(cVal, function (i) {
            cValInt[i] = parseInt(cVal[i]);
        });

        // get the dropdown items and then construct the listbox
        Promise.resolve(_getDdlData(url, ddlCacheCtrlID, durationInMins, isSetCache))
            .then(function (data) {
                DevUtils.logGroupCollapsed('_createDropcheckAll(): creating listbox for ID [' + selectId + ']', 'A11yDropcheck._createDropcheckAll()', _LOG_COLOR);
                _logDebug('data:', data);

                if (data.length > 0) {
                    $.each(data, function (key, val) {
                        let optionId = ddlId + '_' + val[textValue];
                        let label = escapeHtml(val[textField]);
                        let isValInArrayOrChecked = jQuery.inArray(val[textValue], cVal) != '-1' || jQuery.inArray(val[textValue], cValInt) != '-1';  // NOSONAR
                        let ifBlockIndex;

                        if (key === 0 && isSelectRequired) {
                            let selectAllId = ddlId + '_-1';
                            _logDebug('creating Select All option [' + selectId + '] optionId: [' + selectAllId + ']');

                            htm = htm + "<div class='noDataMessage' style='width:100%;'>No Records</div>";

                            if (isCheckAll) {
                                htm = htm + "<li id='" + selectAllId + "' role='option' aria-selected='true' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='Select All'>";
                            }
                            else {
                                htm = htm + "<li id='" + selectAllId + "' role='option' aria-selected='false' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='Select All'>";
                            }
                            htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown SelMode " + 'selectallid ' + "' name='" + ddlId + "' " + checkedState + "  id='" + ddlId + "' value='" + "-1" + "' onchange=\"checkall(this,0,'" + controlType + "','" + ddlId + "')\" aria-label='Select All' />";

                            htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"checkall(this,0,'" + controlType + "','" + ddlId + "')\" style='curosr:pointer;'>";
                            htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                            htm = htm + "Select All</div><br/>";
                            htm = htm + "</li>";
                        }

                        if (isValInArrayOrChecked || val.Selected === true || val.IsChecked === true || isCheckAll === true) {
                            ifBlockIndex = 1;

                            htm = htm + "<li id='" + optionId + "' role='option' aria-selected='true' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";

                            if (ddlId === 'ddlDynaColumnId') {
                                htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown SelMode " + ddlId + "' name='" + ddlId + "' " + checkedState + " id='" + ddlId + "' value='" + val[textValue] + "' checked='checked' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "');unSelectCheckBox(this)\" aria-label='" + label + "' />";
                            } else {
                                htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown SelMode " + ddlId + "' name='" + ddlId + "' " + checkedState + "  id='" + ddlId + "' value='" + val[textValue] + "' checked='checked' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                            }

                            htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                            htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                            htm = htm + label + "</div><br/>";
                            htm = htm + "</li>";
                        } else if (ddlId === 'ddlDynaColumnId') {
                            ifBlockIndex = 2;

                            htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                            htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown SelMode" + ddlId + "' name='" + ddlId + "' " + checkedState + "  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "');unSelectCheckBox(this)\" aria-label='" + label + "' />";
                            htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                            htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                            htm = htm + label + "</div><br/>";
                            htm = htm + "</li>";
                        } else {
                            ifBlockIndex = 3;

                            k++;
                            htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                            htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown SelMode " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"clicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                            htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                            htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                            htm = htm + label + "</div><br/>";
                            htm = htm + "</li>";
                        }

                        _logDebug(ifBlockIndex + ' [' + selectId + '] optionId: [' + optionId + ']');
                    });
                } else {
                    _logDebug('No data found');
                    htm = htm + "No Records";
                }

                DevUtils.logGroupEnd();

                htm = htm + "</ul>";
                $(dropcheckContainer).html('');
                safeFind(dropcheckContainer).append(htm);

                _addA11yInstructionsForSR(dropcheckContainer);

                _dropcheckClickedByProgram(ddlId, 0, controlType, maxSelectionsToDisplay);

                if (callbackFunction) {
                    callbackFunction();
                }
            });

        if (k === 0) {
            $(".selectallid", "#" + selectId).prop("checked", true);
        }

        _setupAjax(!isAsync, true);

        // hide the dropdown on mouseleave
        $(document).off('mouseleave', '#' + selectId).on('mouseleave', '#' + selectId, function () {
            if ($(this).attr('type') !== 'checkbox') {
                $('.divTabHtmlMultiDropDown').removeClass('color-MultiSelectDdl');

                var ctrlId = $(this).attr('data-ddlId');
                var ctrlType = $(this).attr('data-CtrlType');

                _dropcheckColumnSelectedShow(ctrlId, 0, ctrlType, maxSelectionsToDisplay);

                $(".dropcheck").hide();

                _dropCheckTriggerBtnToggleExpanded($('#' + triggerBtnId), listboxId, inputId);
            }
        });
    }

    /**
     * Function to de-select all items in a dropcheck
     * @param {any} selectId
     * @param {any} controlType - type of control checkbox or radio
     * @param {string} ddlId - ID of the input field, if different than the selectId container
     */
    function _deselectAll(selectId, controlType, ddlId, refddlId) {
        let items = $('#' + selectId + ' ul.dropcheck li');
        let firstItem = $(items).get(0);
        if (firstItem && $(firstItem).find('input.selectallid').length > 0) {
            let inputElem = $(firstItem).find('input');
            if ($(inputElem).is(':checked')) {
                let childItemToClick = $(firstItem).find('.ah-dropcheck-item-text')[0];
                _checkAll(childItemToClick, 0, controlType, ddlId || selectId);
                _dropcheckItemClicked(firstItem, controlType);

                $(firstItem).attr('aria-selected', false);
            }
        } else {
            $(items).each(function (index, item) {
                let inputElem = $(item).find('input');
                if ($(inputElem).is(':checked')) {
                    let childItemToClick = $(item).find('.ah-dropcheck-item-text')[0];
                    spnclicked(childItemToClick, 0, controlType, refddlId);
                    _dropcheckItemClicked(item, controlType);
                }
            });
        }
    }

    /**
     * This function was moved from GCHomeCommon.clicked(), and gets called when a checkbox/radio value is changed/clicked. Some of the
     * variables referenced inside this function are declared elsewhere (some other JS or CSHTML file), which is why SONAR is complaining.
     * @param {any} ctrl - the dropcheck component
     * @param {any} iscount
     * @param {any} controlType - type of dropcheck (checkbox or radio)
     */
    function _dropcheckClicked(ctrl, iscount, controlType) {
        _logDebug('dropcheckClicked() [' + $(ctrl).attr('id') + ']', { ctrl: ctrl, iscount: iscount, controlType: controlType });

        $('.divTabHtmlMultiDropDown').removeClass('color-MultiSelectDdl');

        if ($(ctrl).prop('title') !== 'Select All' && $('.selectallid').is(':checked') === true && $(ctrl).is(':checked') === false) {
            let selectAllChk = $(ctrl).closest('ul.dropcheck').find('.selectallid');
            selectAllChk.prop('checked', false);
            selectAllChk.closest('li').attr('aria-selected', false);
        }

        controlType = controlType === undefined ? 'checkbox' : controlType;

        var elementId = ctrl.id.replace('spn', '');

        _dropcheckColumnSelectedShow(elementId, iscount, controlType);

        switch (ctrl.id) {
            case 'ddlWLMLobId':
            case 'ddlWLMLobIdspn':
                isLOBChecked = true;    // NOSONAR (this variable is declared in a different file)
                break;
            case 'ddlWLMPlanId':
            case 'ddlWLMPlanIdspn':
                isPlanChecked = true;   // NOSONAR (this variable is declared in a different file)
                break;
            case 'ddlProfileId':
            case 'ddlProfileIdspn':
                if (typeof (isProfileChecked) != 'undefined') {
                    isProfileChecked = true;
                }
                break;
            case 'ddlFCMBRiskTypeId':
            case 'ddlFCMBRiskTypeIdspn':
                isFCMBRiskTypeChecked = true;   // NOSONAR (this variable is declared in a different file)
                break;
            case 'ddlACSRiskTypeId':
            case 'ddlACSRiskTypeIdspn':
                isACSRiskTypeChecked = true;    // NOSONAR (this variable is declared in a different file)
                break;
            case 'ddlKSRiskTypeId':
            case 'ddlKSRiskTypeIdspn':
                isKSRiskTypeChecked = true;     // NOSONAR (this variable is declared in a different file)
                break;
            case 'ddlMSCareActivityTypeEditId':
            case 'ddlMSCareActivityTypeEditIdspn':
                ManageScriptEditBindPrimaryCareActivityType();
                break;
            case 'ddlEnableOrDisableAppConfigMultiSelectId':
            case 'ddlEnableOrDisableAppConfigMultiSelectIdspn':
                var checkedItem = $('#AppConfigurations .chkAppConfigurations:checked');
                if (checkedItem.data('fieldkey') === 'PROVIDER_PORTAL_MEMBER_INDEX_SEARCH' && $('#ddlEnableOrDisableAppConfigMultiSelectIdhdn').val() !== '' && $('#ddlEnableOrDisableAppConfigMultiSelectIdhdn').val().split(',').length > 2) {
                    if ($(ctrl).hasClass('divTabHtmlMultiDropDown')) {
                        $(ctrl).prev('input[type=' + controlType + ']').prop('checked', false);
                    } else {
                        $(ctrl).prop('checked', false);
                    }
                    alert('Maximum 2 records can be selected');
                }
                break;
            default:
                // do nothing (this is here for Sonar)
        }

        switch (elementId) {
            case 'divClientName':
            case 'divDescDropdown':
            case 'divRace':
            case 'divStateDropdown':
            case 'divCountyDropdown':
            case 'divStatusDropdown':
                enableDisableFilterButtons(false, true);
                break;
            case 'ddlCSMapUserId':
                enableDisableApplyButton();
                break;
            case 'ddlCSWQId':
            case 'ddlCSRoleId':
                loadExistingUsers(elementId);
                break;
            case 'ddlCSExeUserId':
                resetBtnvValues(elementId);
                break;
            case 'ddlMapUserId':
                enableDisableSearchButton();
                break;
            case 'ddlMedView':
                loadMedicationSearchByDropodown();
                break;
            default:
                // do nothing (this is here for Sonar)
        }

        // keep aria-selected updated on the <li> item element
        if (controlType === 'checkbox') {
            $(ctrl).parent().attr('aria-selected', $(ctrl).is(':checked') ? 'true' : 'false');
        }
    }

    /**
     * This function was moved from GCHomeCommon.js. It's not really clear what it's actual purpose is.
     * @param {any} ctrlId - ID of the dropcheck component
     * @param {any} isCount - max number of items that can be selected?
     * @param {any} controlType - radio or checkbox
     */
    function _dropcheckClickedByProgram(ctrlId, isCount, controlType, maxSelectionsToDisplay) {     // NOSONAR
        _logDebug('dropcheckClickedByProgram() ctrlId[' + ctrlId + ']', { ctrlId: ctrlId, isCount: isCount, controlType: controlType });

        controlType = controlType === undefined ? 'checkbox' : controlType;

        var itemArray = [];
        var elemId = ctrlId;
        var chkboxes = $('#' + elemId + 'chk').find('input[type="' + controlType + '"]:checked').not('.selectallid');
        if (chkboxes.length > 0) {
            var selectedIds = '';
            var selectedVals = '';

            $('#' + elemId + 'chk').find('input[type="' + controlType + '"]:checked').not('.selectallid').each(function () {
                itemArray = onOptionCheck($(this), elemId);
                selectedIds = selectedIds === "" ? itemArray[0] : selectedIds + "," + itemArray[0];
                selectedVals = selectedVals === "" ? itemArray[1] : selectedVals + ", " + itemArray[1];
            });

            $('#' + elemId + 'hdn').val(selectedIds);
            $('#' + elemId + 'hdntxt').val(selectedVals);

            if (isCount) {
                $('#' + elemId + 'val').attr('value', selectedIds);
            } else {
                maxSelectionsToDisplay = maxSelectionsToDisplay ? maxSelectionsToDisplay : 3;
                if (chkboxes.length >= 1 && chkboxes.length <= maxSelectionsToDisplay) {
                    if (selectedIds === 'Empty') {
                        selectedVals = '  ';
                    }
                    $('#' + elemId + 'val').attr('placeholder', selectedVals);
                    $('#' + elemId + 'val').attr('title', selectedVals);
                    $('#' + elemId + 'Span').text(selectedVals);
                } else {
                    $('#' + elemId + 'val').val('');
                    $('#' + elemId + 'val').attr('placeholder', chkboxes.length + ' Items Checked');
                    $('#' + elemId + 'val').attr('title', chkboxes.length + ' Items Checked');
                    $('#' + elemId + 'Span').text(chkboxes.length + ' Items Checked');
                }

                if (elemId === 'ddlMaxOpportunityCount' && chkboxes.length >= 2) {
                    $('#' + elemId + 'val').attr('placeholder', chkboxes.length + ' Items Checked');
                    $('#' + elemId + 'val').attr('title', chkboxes.length + ' Items Checked');
                }
            }
        } else {
            $('#' + elemId + 'val').attr('placeholder', 'Select');
            $('#' + elemId + 'val').attr('title', 'Select');
            $('#' + elemId + 'Span').text('');
        }
    }

    /**
     * Function to handle displaying the dropcheck dropdown and properly setting the selected items. This function
     * is a refactored version from GCHomeCommon.ddlcolumnselectedshow()
     * @param {any} elemId
     * @param {any} isCount
     * @param {any} controlType
     */
    function _dropcheckColumnSelectedShow(elemId, isCount, controlType, maxSelectionsToDisplay) {
        _logDebug('_dropcheckColumnSelectedShow() elemId: [' + elemId + ']');

        controlType = controlType ? controlType : 'checkbox';
        var chkboxes = $("#" + elemId + 'chk').find('input[type=' + controlType + ']:checked').not('.selectallid');
        chkboxes = chkboxes.not('#includeMedSensDignosis');
        if (chkboxes.length > 0) {
            if (elemId === 'ddlCareorganization' && $('#ddlSearchBy option:selected').text() !== 'TIN') {
                $('#btnHideCareOrg').hide();
                $('#btnGoCareOrg').show();
            } else if (elemId === 'ddlCareorganization') {
                $('#btnHideCareOrg').hide();
                $('#btnGoCareOrg').hide();
            } else {
                // do nothing special (this is here for Sonar)
            }

            _processSelectedItems(elemId, isCount, controlType, chkboxes, maxSelectionsToDisplay);
        } else {
            _processUnselectedItems(elemId);
        }

        $('#' + elemId + 'val').val('');
    }

    /**
     * Function triggered when a user clicks on a dropcheck item in the listbox
     * @param {any} itemElem - the item clicked
     * @param {any} controlType - type of control (checkbox or radio)
     */
    function _dropcheckItemClicked(itemElem, controlType) {
        _logDebug('dropcheckItemClicked()', { itemElem: itemElem });
        let inputElem = $(itemElem).find('input')[0];
        _dropcheckClicked(inputElem, 0, controlType);
    }

    /**
     * Function to keep the aria-activedescendant attribute in sync when an item is highlighted in the listbox
     * @param {any} ctrlId - the dropcheck component ID
     * @param {any} selectedCheckbox
     */
    function _dropcheckSetAriaActiveDescendant(ctrlId, selectedCheckbox) {
        _logDebug('dropcheckSetAriaActiveDescendant() ctrlId: [' + ctrlId + ']', { selectedCheckbox: selectedCheckbox } );

        let optionItem = $(selectedCheckbox).parent();
        let triggerBtn = $("#" + ctrlId.replace('val', '') + "Btn");
        $(triggerBtn).attr('aria-activedescendant', $(optionItem).attr('id'));
    }

    /**
     * Function called when the dropcheck trigger button is clicked (i.e. the chevron)
     * @param {any} triggerBtn - the trigger button element
     * @param {any} inputId - the ID of the input field
     * @param {any} listboxId - the ID of the dropcheck listbox
     */
    function _dropcheckTriggerBtnOnClick(event, triggerBtn, inputId, listboxId) {
        if (document.activeElement.id === triggerBtn.id) {
            $('#' + inputId).trigger('click');
            _dropCheckTriggerBtnToggleExpanded(triggerBtn, listboxId, inputId);
        }

        event.preventDefault();
    }

    /**
     * Function to process up/right & down/left arrow events inside the dropcheck listbox
     * @param {any} ctrl - the currently selected item in the listbox
     * @param {any} keyCodePressed - the pressed key
     * @param {any} e - the event obj
     */
    function _navigateListboxWithArrowKeys(ctrl, keyCodePressed, e) {
        _logDebug('navigateListboxWithArrowKeys()', { ctrl: ctrl, keyCodePressed: keyCodePressed, e: e });

        let index = $('#' + $(ctrl).attr('id') + 'chk').find('.SelMode:visible').index(ctrl);

        switch (keyCodePressed) {
            case A11yUtil.KEYCODES.left:
            case A11yUtil.KEYCODES.up:
                index = index === 0 ? 1 : index;
                _navigateToCheckbox(ctrl, parseInt(index) - 1, e);
                break;
            case A11yUtil.KEYCODES.right:
            case A11yUtil.KEYCODES.down:
                index = $('#' + $(ctrl).attr('id') + 'chk').find('.SelMode:visible').length - 1 === index ? index - 1 : index;
                _navigateToCheckbox(ctrl, parseInt(index) + 1, e);
                break;
            default:
                // do nothing special (this is here for Sonar)
        }
    }

    /**
     * Function to programmatically select all dropdown items
     * @param {any} selectId
     * @param {any} controlType - type of control checkbox or radio
     */
    function _selectAllItems(selectId, controlType) {
        _logDebug('selectAllItems()', { selectId: selectId, controlType: controlType });

        let items = $('#' + selectId + ' ul.dropcheck li');
        let firstItem = items.get(0);

        if (firstItem && $(firstItem).find('input').attr('value') === '-1' && $(firstItem).attr('aria-selected') === 'false') {
            // if this has a "Select All" option, just trigger a click on the first option (i.e., the Select All option)
            $(firstItem).find('.ah-dropcheck-item-text').trigger('click');
        } else if (firstItem && $(firstItem).find('input').attr('value') !== '-1') {
            $(items).each(function (index) {
                _selectItemByIndex(selectId, index, controlType);
            });
        } else {
            // do nothing (this is here for Sonar)
        }
    }

    /**
     * Function to programatically select a dropdown item
     * @param {any} selectId
     * @param {any} index - zero-based index of the item to select
     * @param {any} controlType - type of control checkbox or radio
     */
    function _selectItemByIndex(selectId, index, controlType) {
        _logDebug('selectItemByIndex()', { selectId: selectId, index: index, controlType: controlType });

        let items = $('#' + selectId + ' ul.dropcheck li');
        let parentItemToClick = $(items).get(index);
        let childItemToClick = $(parentItemToClick).find('.ah-dropcheck-item-text')[0];

        // the dropchecks have two click handlers - one for the outer <li> element and one for the inner child elem; click 'em both
        spnclicked(childItemToClick, 0, controlType);
        _dropcheckItemClicked(parentItemToClick, controlType);
    }

    /**
     *  Function to set a dropcheck disabled. A disable dropcheck will let a user open the dropdown, but they cannot
     *  select or deselect any options in the dropdown. There are also some styling differences to let the user know
     *  it is disabled
     * @param {any} selectId - the dropcheck component ID
     */
    function _setDisabled(selectId) {
        setTimeout(function () {
            $('#' + selectId).addClass('ah-dropcheck-disabled');
            $('#' + selectId + ' ul li input').attr('disabled', 'disabled');
            $('#' + selectId + ' ul li .ah-dropcheck-item-text').removeAttr('onClick');
        });
    }

    /**
     * Function to show the dropdown listbox of a dropcheck element
     * @param {any} ctrl - the dropcheck component
     */
    function _showDrop(ctrl, parEle) {
        _logDebug('showDrop() ctrl:', ctrl);

        let parentSelector = parEle === undefined ? '' : parEle;
        let elementId = ctrl.id;
        let eleclass = $(ctrl, parentSelector).parent()[0].className;
        let setOffsetTop = /insideSelect/i.test(eleclass);
        let isAhInput = /ah-input/i.test(eleclass);
        let useParentHeight = /useParentHeight/i.test(eleclass);
        let parenteleid = elementId.replace("Idval", "");
        let triggerBtnId = elementId.replace("val", "Btn");
        let widthList = $('#' + parenteleid, parentSelector).attr('data-CtrlListWidth') ? $('#' + parenteleid, parentSelector).attr('data-CtrlListWidth') : $('#' + elementId, parentSelector).parent().attr('data-CtrlListWidth');
        let width = $('#' + parenteleid, parentSelector).width();

        $(ctrl, parentSelector).attr('aria-expanded', 'true');
        $('#' + triggerBtnId, parentSelector).attr('aria-expanded', 'true');

        if (widthList !== undefined) {
            width = widthList;
        }

        elementId = elementId.replace("val", "");
        $('#' + elementId + 'chk').attr('style', 'width:' + width + 'px !important;');
        $('#' + elementId + 'chk').show();

        if ($('#' + elementId + "val").val() === '') {
            let kids = $('#' + elementId + 'chk').children();
            $(kids).show();     // show the <li> element
            $(kids).children().show();  // show the elements under the <li> element
        }

        if ($("#" + elementId + "chk .selectallid").length) {
            $("#" + elementId + "chk .noDataMessage").hide();
        }

        if ($(ctrl).val() === "Select") {
            $(ctrl).val('');
        }

        let checkBoxes = $('#' + elementId + 'chk').find('input[type="checkbox"]:visible:checked').not('.selectallid');
        let totalCheckBoxes = $('#' + elementId + 'chk').find('input[type="checkbox"]:visible').not('.selectallid');
        if ($('.selectallid', '#' + elementId + 'chk').length) {
            if (checkBoxes.length === totalCheckBoxes.length && totalCheckBoxes.length !== 0) {
                $('.selectallid', '#' + elementId + 'chk').prop('checked', true);
            } else {
                $('.selectallid', '#' + elementId + 'chk').prop('checked', false);
            }
        }

        if (isAhInput) {
            $('#' + elementId + 'chk').offset({ top: ($(ctrl).offset().top + 43), left: $(ctrl).offset().left });
        } else if (setOffsetTop) {
            $('#' + elementId + 'chk').offset({ top: ($(ctrl).offset().top + 35), left: $('#' + elementId + 'chk').offset().left });
        } else if (useParentHeight) {
            $('#' + elementId + 'chk').offset({ top: ($(ctrl).offset().top + $(ctrl).parent().height()), left: $('#' + elementId + 'chk').offset().left });
        } else {
            $('#' + elementId + 'chk').offset({ top: ($(ctrl).offset().top + 21), left: $('#' + elementId + 'chk').offset().left });
        }
    }

    /**
     * Function to process data typed into the dropcheck text field (for searching)
     * @param {any} ctrl - the dropcheck component
     */
    function _textChanged(ctrl, evt) {
        if (evt && evt.keyCode === A11yUtil.KEYCODES.enter) {
            A11yDropcheck.showDrop(ctrl);
        } else {
            // filter the search results based on the text entered
            _logDebug('textChanged()', { ctrl: ctrl });

            let elementId = ctrl.id;
            let elements = $(ctrl);
            let str = elements.data("id");

            // shows the dropdown
            if ($(ctrl).is($('.ah-dropcheck-text'))) {
                // open it like this for a11y-converted dropchecks (calling this will correctly adjust the location of the dropdown)
                A11yDropcheck.showDrop(ctrl);
            } else {
                // continue to open it the old way for legacy dropchecks
                $("#" + str + "chk").show();
            }

            $("." + str).each(function () {
                let e1 = $(this).next();
                let patt = new RegExp(elements.val().toLowerCase());
                let res = patt.test(e1.text().toLowerCase());

                if (res) {
                    if ($(this).parent().is($('li'))) {
                        // needed for new dropchecks where the checkbox is inside a <li> element
                        $(this).parent().show();
                    } else {
                        // need for legacy dropchecks where the checkbox is by itself
                        $(this).show();
                    }

                    e1.show();
                    e1.next().show();
                } else {
                    if ($(this).parent().is($('li'))) {
                        // needed for new dropchecks where the checkbox is inside a <li> element
                        $(this).parent().hide();
                    } else {
                        // need for legacy dropchecks where the checkbox is by itself
                        $(this).hide();
                    }

                    e1.hide();
                    e1.next().hide();
                }
            });

            elementId = elementId.replace('val', '');
            let checkBoxes = $("#" + elementId + "chk").find('input[type="checkbox"]:checked:visible').not('.selectallid');
            let totalCheckBoxes = $("#" + elementId + "chk").find('input[type="checkbox"]:visible').not('.selectallid');
            if ($('.selectallid', "#" + elementId + "chk").length) {
                if (checkBoxes.length === totalCheckBoxes.length && totalCheckBoxes.length !== 0) {
                    $('.selectallid', "#" + elementId + "chk").prop('checked', true);
                } else {
                    $('.selectallid', "#" + elementId + "chk").prop('checked', false);
                }
            }

            // To remove 'select all' upon zero results
            if (totalCheckBoxes.length === 0 && $("#" + elementId + "chk .selectallid").length) {
                $("#" + elementId + "chk .noDataMessage").show();
                $("#" + elementId + "chk .selectallid").parent().hide();
            } else {
                $("#" + elementId + "chk .noDataMessage").hide();
                $("#" + elementId + "chk .selectallid").parent().show();
            }

            // totalCheckBoxes is the number of results found... use it to update the live region announced to SR users, but only do it for the a11y-converted dropchecks
            let dropcheckContainer = $(ctrl).parent();
            if (totalCheckBoxes && $(dropcheckContainer).is($('.ah-dropcheck'))) {
                let resultsText;
                let resultsCount = $(dropcheckContainer).find('.results-count');
                let numItems = totalCheckBoxes.length;

                $(resultsCount).text('');

                if (numItems === 1) {
                    resultsText = 'One result found';
                } else if (numItems > 1) {
                    resultsText = numItems + ' results found';
                } else {
                    resultsText = 'No results found';
                }

                // update the live region with the num results found so the SR announces it
                $(resultsCount).text(resultsText);
            }
        }
    }

    ///////////////////////
    // private functions //
    ///////////////////////

    /**
     * Adds regions to the dropcheck for a11y screen reader instructions and search result updates
     */
    function _addA11yInstructionsForSR(dropcheckContainer) {
        let selectId = $(dropcheckContainer).attr('id');
        let inputElem = $(dropcheckContainer).find('input.ah-dropcheck-text');

        // add SR instructions on how to use autocompletes
        $(dropcheckContainer).append('<div id="' + selectId + '_instructions" style="display:none;">Begin typing to search, use arrow keys to navigate search results, Spacebar to select.</div>');
        $(inputElem).attr('aria-describedby', selectId + '_instructions');

        // create the live "X results found" region
        $(dropcheckContainer).append('<div id="' + selectId + '_resultsFound" class="results-count sr-only" aria-live="assertive"></div>');
    }
    /**
     * This function is for internal use (to this class only). To programmatically select all items, use A11yDropcheck.selectAllItems()
     */
    function _checkAll(ctrl, isCount, controlType, ddlId) {

        _logDebug('checkAll() for ID [' + ddlId + ']', { ctrl: ctrl, isCount: isCount, controlType: controlType });

        let ischecked = false;
        let tagName = "";
        if (ctrl instanceof HTMLElement) {
            tagName = $.fn.setStringOnUndefinedOrNull(ctrl.tagName).toLowerCase();
        }

        if ($(ctrl).attr("type") !== "checkbox") {
            ctrl = $(ctrl).prev();
            ischecked = !$(ctrl).is(':checked');
            $(ctrl).prop("checked", ischecked);
        } else {
            ischecked = $(ctrl).is(':checked');
        }

        // set all the checkboxes as selected or not
        if ($(ctrl).closest('ul').is(':visible')) {
            /*
             * if the <ul> list element is visible, then it's possible the user may have filtered the list and we need to only
             * choose the visible list items, i.e., not the items that have been filtered out of view
             */
            $("." + ddlId + ":visible").prop('checked', ischecked);
            $("." + ddlId + ":visible").parent().attr('aria-selected', ischecked);
        } else {
            /*
             * if the <ul> list element is not visible, then the user cannot have filtered (this is a situation where we are programmatically selecting
             * them all), and we need to choose all list items (and we cannot use:visible because none will be visible if the list is not displayed)
             */
            $("." + ddlId).prop('checked', ischecked);
            $("." + ddlId).parent().attr('aria-selected', ischecked);
        }

        controlType = controlType ? controlType : 'checkbox';

        var eleId = $(ctrl).attr('id').replace('spn', '');

        _dropcheckColumnSelectedShow(eleId, isCount, controlType);

        switch (ddlId) {
            case 'ddlPPGdLnAssessmentId':
                if (tagName === 'div') {
                    populateChildAssessmentdll();
                }
                break;
            case 'divClientName':
            case 'divDescDropdown':
            case 'divRace':
            case 'divStateDropdown':
            case 'divCountyDropdown':
            case 'divStatusDropdown':
                enableDisableFilterButtons(false, true);
                break;
            case 'ddlCSMapUserId':
                enableDisableApplyButton();
                break;
            case 'ddlCSWQId':
            case 'ddlCSRoleId':
                loadExistingUsers(ddlid);
                break;
            case 'ddlCSExeUserId':
                resetBtnvValues(ddlId);
                break;
            case 'ddlMapUserId':
                enableDisableSearchButton();
                break;
            case 'divReimbursementMethod':
                onChangeSCAControlValidationSCAType();
                break;
            default:
                if (eleId === 'ddlMaxOpportunityCount') {
                    onChangeMaxOppCount();
                }
        }
    }

    // function to construct the html for the items in the dropdown listbox
    function _constructDropcheckList(selectId, data, ddlConfigObj) {
        DevUtils.logGroupCollapsed('constructDropcheckList(): creating listbox for ID [' + selectId + ']', 'A11yDropcheck.constructDropcheckList()', _LOG_COLOR);
        _logDebug('configObj:', { ddlConfigObj: ddlConfigObj, data: data });

        let dropcheckContainer = $("#" + selectId);
        let ddlId = ddlConfigObj.ddlId;
        let cValues = ddlConfigObj.cValues;
        let textField = ddlConfigObj.textField;
        let textValue = ddlConfigObj.textValue;
        let controlType = ddlConfigObj.controlType;
        let htm = ddlConfigObj.htm;
        let isCheckAll = ddlConfigObj.isCheckAll;
        let maxSelectionsToDisplay = ddlConfigObj.maxSelectionsToDisplay;

        if (data && data.length > 0) {
            $.each(data, function (key, val) {
                let optionId = ddlId + '_' + val[textValue];
                let label = escapeHtml(val[textField]);
                let isValInArrayOrChecked = (val[textValue] && jQuery.inArray(val[textValue].toString(), cValues) !== -1) || val.IsChecked;
                let ifBlockIndex;

                if (isCheckAll || isValInArrayOrChecked) {
                    ifBlockIndex = 1;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='true' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";

                    if (ddlId === 'ddlDynaColumnId') {
                        htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' checked='checked' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "');unSelectCheckBox(this);\" aria-label='" + label + "' />";
                    } else {
                        htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' checked='checked' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    }

                    htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    if (controlType !== 'radio') {
                        htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    }
                    htm = htm + label + "</div><br/>";
                    htm = htm + "</li>";
                } else if (ddlId === "ddlDynaColumnId") {
                    ifBlockIndex = 2;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "');unSelectCheckBox(this);\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    htm = htm + label + "</div><br/>";
                    htm = htm + "</li>";
                } else if (ddlId === 'selectSectionsForPrintId' && val[textField] === 'Care Plan Notes') {
                    ifBlockIndex = 3;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' class='ah-dropcheck-radio-parent' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode FullcarePlanNotes " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown FullcarePlanNotes ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    htm = htm + label + "</div><br/>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesFCP' class='clsFCPNotes' id='fullNotes' value='1'><div class='ah-radio-div-label clsFCPNotesText'>Full note history</div></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesFCP' class='clsFCPNotes' id='latestNotes' value='2'><div class='ah-radio-div-label clsFCPNotesText'>Latest notes for selected care plans</div></div><br/>";
                    htm = htm + "</li>";
                } else if (ddlId === 'selectSectionsForPrintId' && val[textField] === 'Approved Service Opportunities') {
                    ifBlockIndex = 4;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' class='ah-dropcheck-radio-parent' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode approvedServiceOpp " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown approvedServiceOpp ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    htm = htm + label + "</div><br/>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesASO' class='clsASONotes' id='asoPrimaryNotes' value='1'><div class='ah-radio-div-label clsASONotesText'>Primary note</div></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesASO' class='clsASONotes' id='asoAllNotes' value='2'><div class='ah-radio-div-label clsASONotesText'>All notes</div></div><br/>";
                    htm = htm + "</li>";
                } else if (ddlId === 'selectSectionsForPrintId' && val[textField] === 'Medications' && viewUnifiedMedications) {
                    ifBlockIndex = 5;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' class='ah-dropcheck-radio-parent' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode carePlanMedications " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown carePlanMedications ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    htm = htm + label + "</div><br/>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsMedNotes' id='currentYes' value='1'><div class='cp_ah-radio-div-label clsMedNotesText'>Current-Yes</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsMedNotes' id='currentNewYes' value='2'><div class='cp_ah-radio-div-label clsMedNotesText'>Current-New, Yes</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsMedNotes' id='currentAll' value='3'><div class='cp_ah-radio-div-label clsMedNotesText'>All</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><span class='cpcheckbox ItemMT20'><input type='checkbox' style='margin: 0px 3px 0px 0px;' id='includeMedSensDignosis' value='4'/><span class='checkStyle ItemML5 ItemMT4'></span></span><label for='includeMedSensDignosis' class='cp_ah-radio-div-label diagnosis_checkbox'>Include Medications for Sensitive Diagnosis</label></div><br/>";
                    htm = htm + "</li>";
                } else if (ddlId === 'selectSectionsForPrintId' && val[textField] === 'Signature') {
                    ifBlockIndex = 6;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' class='ah-dropcheck-radio-parent' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode carePlanSignatures " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown carePlanSignatures ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    htm = htm + label + "</div><br/>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsSignatureSelection' id='Last1DaySignature' value='-2' disabled><div class='cp_ah-radio-div-label clsSignatureText'>Last 1 day</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsSignatureSelection' id='Last7daySignature' value='-1' disabled><div class='cp_ah-radio-div-label clsSignatureText'>Last 7 days</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsSignatureSelection' id='Last30daySignature' value='1' disabled><div class='cp_ah-radio-div-label clsSignatureText'>Last 30 days</div></input></div>";
                    htm = htm + "<div class='ah-dropcheck-radio-div'><input type='radio' name='notesM' class='clsSignatureSelection' id='AllSignatures' value='0' disabled><div class='cp_ah-radio-div-label clsSignatureText'>All</div></input></div>";
                    htm = htm + "</li>";
                } else {
                    ifBlockIndex = 7;

                    htm = htm + "<li id='" + optionId + "' role='option' aria-selected='false' style='width:100%;' onclick='A11yDropcheck.dropcheckItemClicked(this, \"" + controlType + "\")' title='" + label + "'>";
                    htm = htm + "<input type='" + controlType + "' class='TabHtmlMultiDropDown dontSerialize SelMode " + ddlId + "' name='" + ddlId + "'  id='" + ddlId + "' value='" + val[textValue] + "' onchange=\"A11yDropcheck.dropcheckClicked(this,0,'" + controlType + "')\" aria-label='" + label + "' />";
                    htm = htm + "<div class='divTabHtmlMultiDropDown ah-dropcheck-item-text' id='" + ddlId + "spn' onclick=\"spnclicked(this,0,'" + controlType + "')\" style='curosr:pointer;'>";
                    if (controlType !== 'radio') {
                        htm = htm + "<i class='ah-dropcheck-selected fas fa-check' aria-hidden='true'></i>";
                    }
                    htm = htm + label + "</div><br/>";
                    htm = htm + "</li>";
                }

                _logDebug(ifBlockIndex + ' [' + selectId + '] optionId: [' + optionId + ']');
            });
        } else {
            _logDebug('No data found');
            htm = htm + "<div class='clear' style='width:98% !important'> No Records </div>";
        }

        htm = htm + "</ul>";
        safeFind(dropcheckContainer).html('').append(htm);

        DevUtils.logGroupEnd();

        // find and give the associated form field label an id so we can reference it for some aria markup
        let inputElem = $('#' + selectId + ' input.ah-dropcheck-text');
        let labelElem = $('label[for="' + $(inputElem).attr('id'));
        let labelId = $(inputElem).attr('id') + '_label';
        $(labelElem).attr('id', labelId);
        // aria-labelledby should reference both the associated form field label, and a sr-only span which holds the selected values
        $('#' + selectId).find('.ah-dropcheck-btn, ul[role="listbox"]').attr('aria-labelledby', labelId + ' ' + ddlId + 'Span');

        _addA11yInstructionsForSR(dropcheckContainer);

        _dropcheckClickedByProgram(ddlId, 0, controlType, maxSelectionsToDisplay);
    }

    // set aria-expanded on the dropcheck trigger button (i.e. the chevron)
    function _dropCheckTriggerBtnToggleExpanded(triggerBtn, listboxId, inputId) {
        _logVerbose('_dropCheckTriggerBtnToggleExpanded', { triggerBtn: triggerBtn, listboxId: listboxId, inputId: inputId });

        setTimeout(function () {
            let listboxElem = $('#' + listboxId);
            if ($(listboxElem).is(':visible')) {
                $(triggerBtn).attr('aria-expanded', 'true');
                $('#' + inputId).attr('aria-expanded', 'true');
            } else {
                $(triggerBtn).attr('aria-expanded', 'false');
                $('#' + inputId).attr('aria-expanded', 'false');
            }
        }, 200);
    }

    // fetches data for the dropdown, either from cache or server
    function _getDdlData(url, ddlCacheCtrlID, durationInMins, isSetCache) {
        let deferred = $.Deferred();
        let ddlCache = null;

        ddlCache = (ddlCacheCtrlID && cacheData(ddlCacheCtrlID)) || null;

        if (ddlCache != null) {
            _logDebug('_getDdlData() - fetched data from cache');
            deferred.resolve(ddlCache.d);
            return ddlCache;
        } else if (!url || url === '') {
            DevUtils.logWarn('url is blank, cannot fetch data');
            deferred.resolve([]);
        } else {
            _logDebug('_getDdlData() - fetching data from server');

            return $.getJSON(url, function (data) {
                if (ddlCacheCtrlID && isSetCache === true) {
                    durationInMins = (durationInMins != null && durationInMins !== undefined && durationInMins !== '' && durationInMins > 0) ? Math.abs(Math.round(durationInMins)) : 30;
                    cacheData({ key: ddlCacheCtrlID, value: data, maxage: durationInMins }, true);
                }
                deferred.resolve(data.d);
            });
        }

        return deferred.promise;
    }

    function _logDebug(message, object) {
        DevUtils.logDebug(message, 'A11yDropcheck', object, _LOG_COLOR);
    }

    function _logVerbose(message, object) {
        DevUtils.logVerbose(message, 'A11yDropcheck', object, _LOG_COLOR);
    }

    // function to move the cursor up or down in the listbox
    function _navigateToCheckbox(ctrl, index, e) {
        $($('#' + $(ctrl).attr('id') + 'chk').find('.SelMode:visible')[index]).next().addClass('color-MultiSelectDdl');

        let selectedCheckbox = $($('#' + $(ctrl).attr('id') + 'chk').find('.SelMode:visible')[index]);
        $(selectedCheckbox).focus();
        e.preventDefault();
        A11yDropcheck.dropcheckSetAriaActiveDescendant($(ctrl).attr('id'), selectedCheckbox);
    }

    /*
     * Function to process the selected items in the dropdown when it is shown. This was broken out from
     * dropcheckColumnSelectedShow() due to Sonar complaining about the level of complexity
     */
    function _processSelectedItems(elemId, isCount, controlType, chkboxes, maxSelectionsToDisplay) {
        var itemArray = [];
        var totalchkboxes = $("#" + elemId + "chk").find('input[type=' + controlType + ']').not(".selectallid");
        var totalchkboxesVisible = $("#" + elemId + "chk").find('input[type=' + controlType + ']:visible').not('.selectallid');
        var chkboxesVisible = $("#" + elemId + "chk").find('input[type=' + controlType + ']:checked:visible').not('.selectallid');

        var parentId = $("#" + elemId + "chk").parent();
        if (parentId.length > 0) {
            var selectId = $('.selectallid', '#' + parentId[0].id);
            if (selectId.length > 0) {
                selectId.prop('checked', chkboxes.length === totalchkboxes.length);
                selectId.closest('li').attr('aria-selected', chkboxes.length === totalchkboxes.length);
            }
        }

        if ($("#" + elemId + "CheckAll").length) {
            $("#" + elemId + "CheckAll").prop('checked', chkboxes.length === totalchkboxes.length);
        }

        if ($('.selectallid', '#' + elemId).length) {
            $('.selectallid', '#' + elemId).prop('checked', chkboxesVisible.length === totalchkboxesVisible.length);
        }

        var selectedIds = '';
        var selectedVals = '';

        // create a list of selected items (ID's and values)
        let hiddenIdElem = $('#' + elemId + 'hdn');
        let dataDelimiter = $(hiddenIdElem).attr('data-delimiter');
        if (dataDelimiter == null || dataDelimiter === undefined || dataDelimiter === '') {
            dataDelimiter = ",";
        }

        $('#' + elemId + 'chk').find('input[type=' + controlType + ']:checked').not('.selectallid, #includeMedSensDignosis').each(function () {
            itemArray = onOptionCheck($(this), elemId);
            selectedIds = selectedIds === '' ? itemArray[0] : selectedIds + dataDelimiter + itemArray[0];
            selectedVals = selectedVals === '' ? itemArray[1] : selectedVals + dataDelimiter + itemArray[1];
        });

        // store off the selected items in hidden form fields
        hiddenIdElem.val(selectedIds);
        $('#' + elemId + 'hdntxt').val(selectedVals);

        // set the placeholder value indicating which items (or how many) are selected
        maxSelectionsToDisplay = maxSelectionsToDisplay ? maxSelectionsToDisplay : 3;
        if (isCount) {
            $('#' + elemId + 'val').attr('placeholder', selectedIds);
            $('#' + elemId + 'val').attr('title', selectedIds);
            $('#' + elemId + 'Span').text(selectedIds);
        } else if (chkboxes.length >= 1 && chkboxes.length <= maxSelectionsToDisplay || (elemId === 'ddlMaxOpportunityCount' && chkboxes.length < 2)) {
            selectedVals = selectedIds === 'Empty' ? '  ' : selectedVals;
            $('#' + elemId + 'val').attr('placeholder', selectedVals);
            $('#' + elemId + 'val').attr('title', selectedVals);
            $('#' + elemId + 'Span').text(selectedVals);
        } else {
            $('#' + elemId + 'val').attr('placeholder', chkboxes.length + ' items checked');
            $('#' + elemId + 'val').attr('title', chkboxes.length + ' items checked');
            $('#' + elemId + 'Span').text(chkboxes.length + ' items checked');
        }

        _logDebug('_processSelectedItems(): ', { itemArray: itemArray, selectedVals: selectedVals, selectedIds: selectedIds });
    }

    /*
     * Function to process the items in the dropdown when no items are selected. This was broken out from
     * dropcheckColumnSelectedShow() due to Sonar complaining about the level of complexity
     */
    function _processUnselectedItems(elemId) {
        if (elemId === 'ddlCareorganization') {
            if ($('#ddlSearchBy option:selected').text() !== 'TIN') {
                $('#btnHideCareOrg').show();
                $('#btnGoCareOrg').hide();

                if ($('#dvServiceLoc').is(":visible")) {
                    CreateDropCheck('ddlServiceLocations', baseURL + '/PopulationHealth/BindServiceLocations?OrgFilterIdval=', 'ddlServiceLocations', 0);
                }

                if ($('#dvNPI').is(":visible")) {
                    CreateDropCheck('ddlGlobalSearchNpi', baseURL + '/PopulationHealth/BindNpi?OrgFilterIdval=', 'ddlGlobalSearchNpi', 0);
                }
            } else {
                $('#btnHideCareOrg').hide();
                $('#btnGoCareOrg').hide();
            }
        }

        if ($('#' + elemId + 'CheckAll').length) {
            $('#' + elemId + 'CheckAll').prop('checked', false);
        }

        $('#' + elemId + 'val').attr('placeholder', 'Select');
        $('#' + elemId + 'val').attr('title', 'Select');
        $('#' + elemId + 'hdn').val('');
        $('#' + elemId + 'hdntxt').val('');
        $('#' + elemId + 'Span').text('');
    }

    function _setupAjax(setupAjax, asyncValue) {
        if (setupAjax) {
            jQuery.ajaxSetup({ async: asyncValue, cache: false });
        }
    }
})();

///////////////////////////////////////////////////////////////////////////
// Legacy functions that have been moved to this JS file for convenience //
///////////////////////////////////////////////////////////////////////////
/**
 * @deprecated - call A11yDropcheck.createDropcheck() instead
 * Function to create the custom dropdown/autocomplete with checkboxes element. This is the legacy function and will make an immediate call
 * to the refactored A11yDropcheck.createDropCheck() function. This function was moved over from GCHomeCommon.js
 *
 * RE: the NOSONAR comment... Sonar is complaining about too many function arguments, but there are too invocations (300+) of this function
 * throughout the app to reasonably change
 */
function CreateDropCheck(selectId, url, ddlId, tbId, cvalues, onkeyupValue, dropdownType, textField, textValue, isAsync, ddlCacheCtrlID, DurationInMins, describedById, callBackFun, spFlag) // NOSONAR
{
    let configObj = {
        url: url,
        ddlId: ddlId,
        tbId: tbId,
        cValues: cvalues,
        dropdownType: dropdownType,
        textField: textField,
        textValue: textValue,
        isAsync: isAsync,
        ddlCacheCtrlID: ddlCacheCtrlID,
        durationInMins: DurationInMins,
        describedById: describedById,
        callbackFunction: typeof (callBackFun) == "function" ? callBackFun : false,
        spFlag: spFlag
    };

    A11yDropcheck.createDropcheck(selectId, configObj);
}

/**
 * @deprecated - call A11yDropcheck.createDropcheckAll() instead
 *
 * RE: the NOSONAR comment... Sonar is complaining about too many function arguments, but there are too invocations (300+) of this function
 * throughout the app to reasonably change
 */
function CreateDropCheckAll(selectid, url, ddlid, tbid, cvalues, onkeyupValue, configvalue, textField, textValue, isAsync, isCheckAll, isSelectRequired, isSetCache, durationInMins, callbackFunction) {  // NOSONAR
    let configObj = {
        url: url,
        ddlId: ddlid,
        tbId: tbid,
        cValues: cvalues,
        dropdownType: configvalue,
        textField: textField,
        textValue: textValue,
        isAsync: isAsync,
        isCheckAll: isCheckAll,
        isSelectRequired: isSelectRequired,
        isSetCache: isSetCache || false,
        durationInMins: durationInMins,
        callbackFunction: callbackFunction
    };

    A11yDropcheck.createDropcheckAll(selectid, configObj);
}

/**
 * @deprecated - call A11yDropcheck.checkAll() instead
 */
function checkall(ctrl, iscount, controlType, ddlid) {
    A11yDropcheck.checkAll(ctrl, iscount, controlType, ddlid);
}

function checkColumns(elem, val) {
    DevUtils.logDebug('validating num checked columns', 'A11yDropcheck.checkColumns()', { elem: elem, val: val }, A11yDropcheck.LOG_COLOR);

    if (!elem.is(':checked')) {
        CmnStatusMessageDisp('myMembersMsg', 'You cannot select more than ' + val + ' columns.', 'Error');
    }
    elem.prop('checked', false);
}

/**
 * @deprecated - call A11yDropcheck.dropcheckClicked() instead
 */
function clicked(ctrl, isCount, controlType) {
    A11yDropcheck.dropcheckColumnSelectedShow(ctrl.id, isCount, controlType);
}

/**
 * @deprecated - call A11yDropcheck.dropcheckClicked() instead
 */
function ddlcolumnselectedshow(eleid, iscount, controlType) {
    DevUtils.logDebug('legacy function ddlcolumnselectedshow() called, calling A11yDropcheck.dropcheckColumnSelectedShow()', 'GCHomeCommon.ddlcolumnselectedshow()', { eleid: eleid, iscount: iscount, controlType: controlType }, 'red');
    A11yDropcheck.dropcheckColumnSelectedShow(eleid, iscount, controlType);
}

/**
 * @deprecated - call A11yDropcheck.clickedbyprg() instead
 */
function clickedbyprg(ctrlId, isCount, controlType) {
    A11yDropcheck.dropcheckClickedByProgram(ctrlId, isCount, controlType);
}

/**
 * @deprecated - call A11yDropcheck.showDrop() instead
 */
function showdrop(ctrl, parentSelector) {
    if (parentSelector) {
        A11yDropcheck.showDrop(ctrl, parentSelector);
    } else {
        A11yDropcheck.showDrop(ctrl);
    }
}

function spnclicked(ctrl, isCount, controlType, refddlId) {
    DevUtils.logDebug('listbox item span clicked', 'A11yDropcheck.spnclicked()', { ctrl: ctrl, isCount: isCount, controlType: controlType }, A11yDropcheck.LOG_COLOR);

    $('.divTabHtmlMultiDropDown').removeClass('color-MultiSelectDdl');
    controlType = controlType ? controlType : 'checkbox';

    var elemId = ctrl.id.replace('spn', '');
    var chkboxes = $("#" + elemId + "chk").find('input[type=' + controlType + ']:checked');
    var elem = $(ctrl).prev("input[type=" + controlType + "]");
    if (elemId === "ddlFilterMyMemberSelection") {
        if (chkboxes.length >= 5 && myMemConfigPSUScore == "True") {    // NOSONAR
            checkColumns(elem, 5);
            return;
        } else if (chkboxes.length >= 6) {
            checkColumns(elem, 6);
            return;
        } else {
            // do nothing (this is here for Sonar)
        }
    } else if (elemId === "ddlDynaColumnId" && chkboxes.length >= 7 && !elem.is(':checked')) {
        CustomAlert('You cannot select more than 7 columns');
        return;
    } else if ((elemId.indexOf('ddlCrPlAddSrPlModifier_') != -1 || elemId.indexOf('ddlCrPlEditSrPlModifier_') != -1) && chkboxes.length >= 5) {  // NOSONAR
        if (!elem.is(':checked')) {
            var lbl = elemId.indexOf('ddlCrPlAddSrPlModifier_') != -1 ? 'lblCrPlAddMemSerPlanUpdStsMsg' : 'spnServicePlanUpdStatusAdd'; // NOSONAR
            CmnStatusMessageDisp(lbl, 'You cannot select more than 5 modifiers.', 'Error');
        }
        elem.prop('checked', false);
        return;
    } else {
        // do nothing (this is here for Sonar)
    }

    elem.prop('checked', !(elem.is(':checked') && controlType !== "radio"));
    elem.focus();

    if (elemId === 'ddlcmpDocTypeId' && chkboxes.length >= 5) {
        elem.prop('checked', false);
        return;
    }

    A11yDropcheck.dropcheckClicked(ctrl, isCount, controlType);

    if ($('#ddlSavedSearch').val() !== "-1" && refddlId !== 'ddlSavedSearch') {   // NOSONAR
        $('#ddlSavedSearch').val('-1');
        $('#chkDefaultSearch').attr('checked', false);
        $('#btnViewSearchParameters').hide();
    }

    if (elemId === "ddlAcStaffIdsId") {
        dataString['CareStaffIdsBySearch'] = $('#ddlAcStaffIdsIdhdn').val();
        $("#AssignCareStaffGrid").data("kendoGrid").dataSource.page(1);
    }

    if (elemId === "ddlPPGdLnAssessmentId") {
        populateChildAssessmentdll();
    }

    if (elemId === "ddlMaxOpportunityCount") {
        onChangeMaxOppCount();
    }
    if (elemId === 'ddlcmpStatusId') {
        onChangeCMPStatus();
    }
    if (elemId === 'ddlcmpScriptId') {
        onChangeCMPScript();
    }
    if (elemId === 'divReimbursementMethod') {
        onChangeSCAControlValidationSCAType();
    }
}

/**
 * @deprecated - call A11yDropcheck.textChanged() instead
 */
function textChanged(ctrl) {
    A11yDropcheck.textChanged(ctrl);
}

/////////////////////////////////////////////////////
// Global Event Handlers related to the Dropchecks //
/////////////////////////////////////////////////////
$(document).off('keydown', '.ah-dropcheck button[role="combobox"]').on('keydown', '.ah-dropcheck button[role="combobox"]', function (e) {
    DevUtils.logDebug('event:', 'A11yDropcheck.keydown(.ah-dropcheck button[role="combobox"])', { keyCode: e.keyCode, e: e }, A11yDropcheck.LOG_COLOR);
    let buttonId = $(this).attr('id');
    let inputId = buttonId.replace('Btn', 'val');
    $('#' + inputId).trigger('keydown', [e]);
});

$(document).off('keydown', '.TabHtmlMultiDropDown').on('keydown', '.TabHtmlMultiDropDown', function (e, triggerEvent) {
    let ctrlId = $(this).attr('Id');
    let triggerBtnId = ctrlId.replace('val', 'Btn');
    let parentCtrl = $("#" + ctrlId.replace('val', 'chk'));
    $('.divTabHtmlMultiDropDown').removeClass('color-MultiSelectDdl');

    let keyCodePressed = triggerEvent && triggerEvent.keyCode ? triggerEvent.keyCode : e.keyCode;

    DevUtils.logDebug('event:', 'A11yDropcheck.keydown(.TabHtmlMultiDropDown)', { keyCodePressed: keyCodePressed, e: e, triggerEvent: triggerEvent }, A11yDropcheck.LOG_COLOR);

    if ((keyCodePressed >= 37 && keyCodePressed <= 40)) {
        let selectedCheckbox;

        if ($(this).hasClass("droptexthide")) {
            A11yDropcheck.showDrop(this);
            $($(parentCtrl).find('.SelMode:visible')[0]).next().addClass('color-MultiSelectDdl');
            selectedCheckbox = $($(parentCtrl).find('.SelMode:visible')[0]);
            selectedCheckbox.focus();

            let containerId = $(parentCtrl).attr('id');

            setTimeout(function () {
                document.getElementById(containerId).scrollTop = 0;
            });

            e.preventDefault();
            A11yDropcheck.dropcheckSetAriaActiveDescendant(ctrlId, selectedCheckbox);
        } else if ($(this).hasClass("SelMode")) {
            A11yDropcheck.navigateListboxWithArrowKeys(this, keyCodePressed, e);
        } else {
            // do nothing (this is here for Sonar)
        }
    } else if (keyCodePressed === A11yUtil.KEYCODES.enter && $(this).hasClass("SelMode")) {
        let isCurrentlyChecked = $(this).prop('checked');

        if (!isCurrentlyChecked) {
            $(this).prop('checked', true);
            A11yDropcheck.dropcheckItemClicked($(this).parent(), $(this).attr('type'));
        }

        if (this.value === '-1') {
            // user hit Enter key for "Select All" option
            if (isCurrentlyChecked) {
                A11yDropcheck.deselectAll($(this).closest('.ah-dropcheck').attr('id'), $(this).attr('type'), ctrlId);
            } else {
                A11yDropcheck.checkAll(this, 0, $(this).attr('type'), ctrlId);
            }
        }

        if (isCurrentlyChecked) {
            $(this).prop('checked', false);
            A11yDropcheck.dropcheckItemClicked($(this).parent(), $(this).attr('type'));
        }

    } else if (keyCodePressed === A11yUtil.KEYCODES.spacebar && $(this).hasClass("SelMode") && this.value === '-1' && $(this).prop('checked')) {
        // user hit Space key for currently-selected "Select All" option (i.e., user wants to deselect all options using the space key)
        setTimeout(function () {
            A11yDropcheck.deselectAll(ctrlId, $(this).attr('type'));
        }, 250);
    } else if (keyCodePressed === A11yUtil.KEYCODES.tab || keyCodePressed === A11yUtil.KEYCODES.escape) {
        $(".dropcheck").hide();
        $(this).attr('aria-expanded', 'false');
        $('#' + triggerBtnId).attr('aria-expanded', 'false');
    } else {
        // do nothing (this is here for Sonar)
    }
});

function _destroyKendoDropdown(ele) {
    //clear dropdown control
    let kendoDdlId = $(ele).attr('id');
    if (kendoDdlId) {
        let kendoDdl = $(ele).data("kendoDropDownList");
        // if for some reason the Kendo has already been destroyed, it will be 'undefined' here
        if (kendoDdl !== undefined) {
            // destroy the Kendo components that are appended to the document body that cannot clearly be associated to the widget (i.e. the expanded dropdown items)
            kendoDdl.destroy();
            $('#' + kendoDdlId + '-list').closest('.k-animation-container').remove();
            $('#' + kendoDdlId + '-list').closest('.k-list-container').remove();
        }
    }
}
